/**
 * FibonacciPi Client Telemetry, Interaction Proofs & Bot Detection Tracker
 *
 * Collects passive client environment signals and natural user interactions
 * (mouse movements, touch events, scroll depth, webdriver status)
 * and dispatches lightweight non-blocking telemetry beacons to the API.
 */

export interface ActivityBeaconPayload {
  action: string
  path?: string
  screen_resolution?: string
  signals?: Record<string, any>
  metadata?: Record<string, any>
  entity_type?: string
  entity_id?: number
}

let interactionCount = 0
let hasMouseMovement = false
let hasTouchInteraction = false
let hasScrollInteraction = false
let listenersAttached = false
let lastBeaconPath = ''

/**
 * Capture human interaction signals silently
 */
function attachInteractionListeners() {
  if (typeof window === 'undefined' || listenersAttached) return
  listenersAttached = true

  const onMouseMove = () => {
    hasMouseMovement = true
    interactionCount++
    window.removeEventListener('mousemove', onMouseMove)
  }

  const onTouch = () => {
    hasTouchInteraction = true
    interactionCount++
    window.removeEventListener('touchstart', onTouch)
  }

  const onScroll = () => {
    hasScrollInteraction = true
    interactionCount++
    window.removeEventListener('scroll', onScroll)
  }

  const onKey = () => {
    interactionCount++
  }

  window.addEventListener('mousemove', onMouseMove, { passive: true })
  window.addEventListener('touchstart', onTouch, { passive: true })
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('keydown', onKey, { passive: true })
}

/**
 * Collect device and environment signals
 */
export function getClientSignals(): Record<string, any> {
  if (typeof window === 'undefined') return {}

  const nav = window.navigator as any
  const screen = window.screen

  return {
    webdriver: Boolean(nav.webdriver),
    has_mouse: hasMouseMovement,
    has_touch: hasTouchInteraction || (nav.maxTouchPoints && nav.maxTouchPoints > 0),
    has_scroll: hasScrollInteraction,
    interaction_count: interactionCount,
    screen_resolution: screen ? `${screen.width}x${screen.height}` : undefined,
    color_depth: screen?.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    device_memory: nav.deviceMemory || undefined,
    hardware_concurrency: nav.hardwareConcurrency || undefined,
    platform: nav.platform,
    languages: nav.languages ? nav.languages.join(',') : nav.language,
  }
}

/**
 * Send an activity beacon to the backend API without blocking page transitions
 */
export function sendActivityBeacon(payload: ActivityBeaconPayload) {
  if (typeof window === 'undefined') return

  attachInteractionListeners()

  const signals = {
    ...getClientSignals(),
    ...(payload.signals || {}),
  }

  const body = JSON.stringify({
    action: payload.action,
    path: payload.path || window.location.pathname,
    screen_resolution: signals.screen_resolution,
    signals,
    metadata: payload.metadata || {},
    entity_type: payload.entity_type,
    entity_id: payload.entity_id,
  })

  // Try navigator.sendBeacon first for non-blocking execution
  const beaconUrl = '/api/v1/analytics/beacon'
  let sent = false

  try {
    if (typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' })
      sent = navigator.sendBeacon(beaconUrl, blob)
    }
  } catch (e) {
    sent = false
  }

  // Fallback to fetch with keepalive
  if (!sent) {
    fetch(beaconUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => {
      // Non-fatal telemetry catch
    })
  }
}

/**
 * Track route view with automatic deduplication
 */
export function trackRouteActivity(path: string) {
  if (path === lastBeaconPath) return
  lastBeaconPath = path

  sendActivityBeacon({
    action: 'page.view',
    path,
    metadata: {
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      title: typeof document !== 'undefined' ? document.title : '',
    },
  })
}

/**
 * Public helper to log custom user actions (e.g. job apply, cv create, search)
 */
export function trackUserAction(
  action: string,
  metadata?: Record<string, any>,
  entityType?: string,
  entityId?: number
) {
  sendActivityBeacon({
    action,
    metadata,
    entity_type: entityType,
    entity_id: entityId,
  })
}
